import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

const AdminHeaderRefreshContext = createContext(null);

export function AdminHeaderRefreshProvider({ children }) {
  const handlerRef = useRef(null);
  const [hasRefresh, setHasRefresh] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const registerRefresh = useCallback((fn) => {
    handlerRef.current = typeof fn === 'function' ? fn : null;
    setHasRefresh(Boolean(fn));
    return () => {
      if (handlerRef.current === fn) {
        handlerRef.current = null;
        setHasRefresh(false);
      }
    };
  }, []);

  const runRefresh = useCallback(async () => {
    if (refreshing) return;
    setRefreshing(true);
    try {
      if (typeof handlerRef.current === 'function') {
        await handlerRef.current();
      } else {
        // Keep Refresh available on every page — hard reload when no page handler.
        window.location.reload();
      }
    } finally {
      setRefreshing(false);
    }
  }, [refreshing]);

  const value = useMemo(
    () => ({
      registerRefresh,
      runRefresh,
      hasRefresh,
      refreshing,
    }),
    [registerRefresh, runRefresh, hasRefresh, refreshing],
  );

  return (
    <AdminHeaderRefreshContext.Provider value={value}>
      {children}
    </AdminHeaderRefreshContext.Provider>
  );
}

export function useAdminHeaderRefresh(loadFn) {
  const ctx = useContext(AdminHeaderRefreshContext);

  useEffect(() => {
    if (!ctx?.registerRefresh || !loadFn) return undefined;
    return ctx.registerRefresh(loadFn);
  }, [ctx, loadFn]);

  return ctx;
}

export function useAdminHeaderRefreshControls() {
  return useContext(AdminHeaderRefreshContext);
}
